// Servicio base para comunicación con la API
// Estructura preparada para cuando tengas el backend

// En Vercel, la API está en la misma URL que el frontend
// Detectar si estamos en producción (Vercel)
const isProduction = window.location.hostname.includes('vercel.app') || 
                     window.location.hostname.includes('vercel.com') ||
                     process.env.NODE_ENV === 'production';

const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (isProduction ? '/api' : 'http://localhost:3001/api');

console.log('🔍 API Configuration:', {
  hostname: window.location.hostname,
  isProduction,
  API_BASE_URL,
  NODE_ENV: process.env.NODE_ENV
});

// Verificar si la API está disponible (solo en producción)
if (isProduction) {
  fetch(`${API_BASE_URL}/health`)
    .then((res) => {
      if (res.ok) {
        console.log('✅ API backend conectada');
      } else {
        console.warn('⚠️ API backend no responde correctamente');
      }
    })
    .catch((err) => {
      console.warn('⚠️ API backend no disponible:', err.message);
    });
}

/**
 * Realiza una petición HTTP con manejo de errores
 */
const fetchAPI = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  // Agregar token de autenticación si existe
  const token = localStorage.getItem('token');
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    console.log('📤 Enviando petición a:', url);
    const response = await fetch(url, config);
    
    console.log('📥 Respuesta recibida:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ 
        error: `Error ${response.status}: ${response.statusText}` 
      }));
      console.error('❌ Error en la respuesta:', errorData);
      throw new Error(errorData.error || errorData.warning || `Error en la respuesta: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Respuesta exitosa:', data);
    return data;
  } catch (error) {
    console.error('❌ Error en fetchAPI:', error);
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('fetch')) {
      throw new Error('No se pudo conectar con el servidor. Verifica que la API esté corriendo.');
    }
    throw error;
  }
};

// ============================================================================
// EMAILS
// ============================================================================

/**
 * Envía un email
 */
export const sendEmailAPI = async (emailData) => {
  console.log('📤 [Frontend] Enviando email:', emailData);
  return fetchAPI('/emails/send', {
    method: 'POST',
    body: JSON.stringify(emailData),
  });
};

/**
 * Obtiene el historial de emails
 */
export const getEmailHistoryAPI = async (filters = {}) => {
  const queryParams = new URLSearchParams(filters).toString();
  const endpoint = queryParams ? `/emails/history?${queryParams}` : '/emails/history';
  return fetchAPI(endpoint);
};

/**
 * Procesa emails nuevos de Gmail manualmente
 */
export const processEmailsAPI = async (reporteId = null) => {
  return fetchAPI('/emails/process', {
    method: 'POST',
    body: JSON.stringify({ reporteId }),
  });
};

// ============================================================================
// STARTUPS
// ============================================================================

/**
 * Obtiene todas las startups
 */
export const getStartups = async () => {
  return fetchAPI('/startups');
};

/**
 * Crea una nueva startup
 */
export const createStartup = async (startupData) => {
  return fetchAPI('/startups', {
    method: 'POST',
    body: JSON.stringify(startupData),
  });
};

/**
 * Actualiza una startup
 */
export const updateStartup = async (startupId, startupData) => {
  return fetchAPI(`/startups/${startupId}`, {
    method: 'PUT',
    body: JSON.stringify(startupData),
  });
};

/**
 * Elimina una startup
 */
export const deleteStartup = async (startupId) => {
  return fetchAPI(`/startups/${startupId}`, {
    method: 'DELETE',
  });
};

// ============================================================================
// REPORTES
// ============================================================================

/**
 * Obtiene todos los reportes
 */
export const getReportes = async () => {
  return fetchAPI('/reportes');
};

/**
 * Crea un nuevo reporte
 */
export const createReporte = async (reporteData) => {
  return fetchAPI('/reportes', {
    method: 'POST',
    body: JSON.stringify(reporteData),
  });
};

/**
 * Obtiene un reporte por ID
 */
export const getReporte = async (reporteId) => {
  return fetchAPI(`/reportes/${reporteId}`);
};

/**
 * Actualiza un reporte
 */
export const updateReporte = async (reporteId, reporteData) => {
  return fetchAPI(`/reportes/${reporteId}`, {
    method: 'PUT',
    body: JSON.stringify(reporteData),
  });
};

/**
 * Elimina un reporte
 */
export const deleteReporte = async (reporteId) => {
  return fetchAPI(`/reportes/${reporteId}`, {
    method: 'DELETE',
  });
};

/**
 * Obtiene el contenido genérico de un reporte (información no dependiente de startups)
 */
export const getReporteContenidoGenerico = async (reporteId) => {
  return fetchAPI(`/reportes/${reporteId}/contenido-generico`);
};

/**
 * Actualiza el contenido genérico de un reporte
 */
export const updateReporteContenidoGenerico = async (reporteId, contenido) => {
  return fetchAPI(`/reportes/${reporteId}/contenido-generico`, {
    method: 'PUT',
    body: JSON.stringify({ contenido }),
  });
};

/**
 * Rellena Comités con IA usando los documentos de Drive seleccionados (por ID).
 * Mantiene la estructura actual y reemplaza fechas y startups extraídos de los documentos.
 * @param {number} reporteId
 * @param {{ fileIds: string[] }} options
 */
export const rellenarDesdeDrive = async (reporteId, options = {}) => {
  return fetchAPI(`/reportes/${reporteId}/rellenar-desde-drive`, {
    method: 'POST',
    body: JSON.stringify({ fileIds: options.fileIds || [] }),
  });
};

/**
 * Obtiene los datos de una startup en un reporte
 */
export const getStartupDataInReporte = async (reporteId, startupId) => {
  return fetchAPI(`/reportes/${reporteId}/startups/${startupId}`);
};

/**
 * Actualiza los datos de una startup en un reporte
 */
export const updateStartupDataInReporte = async (reporteId, startupId, data) => {
  return fetchAPI(`/reportes/${reporteId}/startups/${startupId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Obtiene los archivos adjuntos de una startup en un reporte
 */
export const getAttachments = async (reporteId, startupId) => {
  return fetchAPI(`/reportes/${reporteId}/startups/${startupId}/attachments`);
};

/**
 * Descarga un archivo adjunto
 */
export const downloadAttachment = async (attachmentId) => {
  const isProduction = window.location.hostname.includes('vercel.app') || 
                       window.location.hostname.includes('vercel.com') ||
                       process.env.NODE_ENV === 'production';
  const API_BASE_URL = process.env.REACT_APP_API_URL || 
    (isProduction ? '/api' : 'http://localhost:3001/api');
  
  // Para descargas, abrimos una nueva ventana
  window.open(`${API_BASE_URL}/attachments/${attachmentId}`, '_blank');
};

// ============================================================================
// SOCIOS
// ============================================================================

/**
 * Obtiene todos los socios
 */
export const getSocios = async () => {
  return fetchAPI('/socios');
};

/**
 * Crea un nuevo socio
 */
export const createSocio = async (socioData) => {
  return fetchAPI('/socios', {
    method: 'POST',
    body: JSON.stringify(socioData),
  });
};

/**
 * Actualiza un socio
 */
export const updateSocio = async (socioId, socioData) => {
  return fetchAPI(`/socios/${socioId}`, {
    method: 'PUT',
    body: JSON.stringify(socioData),
  });
};

/**
 * Elimina un socio
 */
export const deleteSocio = async (socioId) => {
  return fetchAPI(`/socios/${socioId}`, {
    method: 'DELETE',
  });
};

// ============================================================================
// SINCRONIZACIÓN EMAIL
// ============================================================================

/**
 * Sincroniza emails con Gmail
 */
export const syncGmail = async () => {
  return fetchAPI('/emails/sync', {
    method: 'POST',
  });
};

/**
 * Procesa respuestas de emails con IA
 */
export const processEmailResponses = async (reporteId) => {
  return fetchAPI(`/reportes/${reporteId}/process-emails`, {
    method: 'POST',
  });
};

// ============================================================================
// AUTENTICACIÓN
// ============================================================================

/**
 * Registra un nuevo usuario
 */
export const register = async (userData) => {
  return fetchAPI('/auth/register', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
};

/**
 * Inicia sesión
 */
export const login = async (email, password) => {
  return fetchAPI('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
};

/**
 * Verifica el token del usuario actual
 */
export const verifyToken = async () => {
  return fetchAPI('/auth/verify');
};

/**
 * Obtiene el perfil del usuario autenticado
 */
export const getProfile = async () => {
  return fetchAPI('/user/profile');
};

/**
 * Actualiza el perfil del usuario autenticado
 */
export const updateProfile = async (profileData) => {
  return fetchAPI('/user/profile', {
    method: 'PUT',
    body: JSON.stringify(profileData),
  });
};

/**
 * Sube el logo del usuario
 */
export const uploadUserLogo = async (logoFile) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result; // Ya incluye "data:image/..."
        const response = await fetchAPI('/user/logo', {
          method: 'POST',
          body: JSON.stringify({ logo: base64 }),
        });
        resolve(response);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(logoFile);
  });
};

/**
 * Obtiene el logo del usuario
 */
export const getUserLogo = async () => {
  return fetchAPI('/user/logo');
};

/**
 * Elimina el logo del usuario
 */
export const deleteUserLogo = async () => {
  return fetchAPI('/user/logo', {
    method: 'DELETE',
  });
};

// ============================================================================
// PLANTILLAS DE EMAIL
// ============================================================================

/**
 * Obtiene todas las plantillas de email
 */
export const getEmailTemplates = async () => {
  return fetchAPI('/email-templates');
};

/**
 * Actualiza las plantillas de email
 */
export const updateEmailTemplates = async (templates) => {
  return fetchAPI('/email-templates', {
    method: 'PUT',
    body: JSON.stringify({ templates }),
  });
};

/**
 * Restaura las plantillas de email a valores por defecto
 */
export const resetEmailTemplates = async () => {
  return fetchAPI('/email-templates/reset', {
    method: 'POST',
  });
};

// ============================================================================
// GMAIL OAUTH
// ============================================================================

/**
 * Obtiene la URL de autorización de Gmail
 */
export const getGmailAuthUrl = async () => {
  return fetchAPI('/gmail/auth');
};

/**
 * Verifica el estado de conexión de Gmail
 */
export const getGmailStatus = async () => {
  return fetchAPI('/gmail/status');
};

/**
 * Desconecta Gmail
 */
export const disconnectGmail = async () => {
  return fetchAPI('/gmail/disconnect', {
    method: 'DELETE',
  });
};

// ============================================================================
// DRIVE API
// ============================================================================

/**
 * Obtiene la URL de autorización de Google Drive
 */
export const getDriveAuthUrl = async () => {
  return fetchAPI('/drive/auth');
};

/**
 * Verifica el estado de conexión de Drive
 */
export const getDriveStatus = async () => {
  return fetchAPI('/drive/status');
};

/**
 * Desconecta Google Drive
 */
export const disconnectDrive = async () => {
  return fetchAPI('/drive/disconnect', {
    method: 'DELETE',
  });
};

/**
 * Lista archivos y carpetas de Drive
 */
export const listDriveFiles = async (folderId = 'root', pageToken = null) => {
  const params = new URLSearchParams({ folderId });
  if (pageToken) {
    params.append('pageToken', pageToken);
  }
  return fetchAPI(`/drive/files?${params.toString()}`);
};

/**
 * Obtiene información de una carpeta específica
 */
export const getDriveFolderInfo = async (folderId) => {
  return fetchAPI(`/drive/folder/${folderId}`);
};

/**
 * Busca carpetas por nombre
 */
export const searchDriveFolders = async (name) => {
  return fetchAPI(`/drive/search-folders?name=${encodeURIComponent(name)}`);
};

/**
 * Guarda la configuración de carpeta raíz
 */
export const saveDriveConfig = async (rootFolderId, rootFolderName) => {
  return fetchAPI('/drive/config', {
    method: 'POST',
    body: JSON.stringify({ rootFolderId, rootFolderName }),
  });
};

/**
 * Obtiene la configuración de carpeta raíz
 */
export const getDriveConfig = async () => {
  return fetchAPI('/drive/config');
};

/**
 * Busca archivos y carpetas en todo Drive
 */
export const searchDriveFiles = async (query, pageToken = null) => {
  const params = new URLSearchParams({ q: query });
  if (pageToken) {
    params.append('pageToken', pageToken);
  }
  return fetchAPI(`/drive/search?${params.toString()}`);
};

const apiService = {
  sendEmailAPI,
  getEmailHistoryAPI,
  getStartups,
  createStartup,
  updateStartup,
  deleteStartup,
  getReportes,
  createReporte,
  getReporte,
  updateReporte,
  deleteReporte,
  getReporteContenidoGenerico,
  updateReporteContenidoGenerico,
  rellenarDesdeDrive,
  getStartupDataInReporte,
  updateStartupDataInReporte,
  getAttachments,
  downloadAttachment,
  getSocios,
  createSocio,
  updateSocio,
  deleteSocio,
  syncGmail,
  processEmailResponses,
  register,
  login,
  verifyToken,
  getProfile,
  updateProfile,
  uploadUserLogo,
  getUserLogo,
  deleteUserLogo,
  getEmailTemplates,
  updateEmailTemplates,
  resetEmailTemplates,
  getGmailAuthUrl,
  getGmailStatus,
  disconnectGmail,
  getDriveAuthUrl,
  getDriveStatus,
  disconnectDrive,
  listDriveFiles,
  getDriveFolderInfo,
  searchDriveFolders,
  saveDriveConfig,
  getDriveConfig,
};

// ============================================================================
// CALENDAR OAUTH
// ============================================================================

/**
 * Obtiene la URL de autorización de Google Calendar
 */
export const getCalendarAuthUrl = async () => {
  return fetchAPI('/calendar/auth');
};

/**
 * Verifica el estado de conexión de Calendar
 */
export const getCalendarStatus = async () => {
  return fetchAPI('/calendar/status');
};

/**
 * Desconecta Google Calendar
 */
export const disconnectCalendar = async () => {
  return fetchAPI('/calendar/disconnect', {
    method: 'DELETE',
  });
};

/**
 * Lista eventos del calendario
 */
export const getCalendarEvents = async (timeMin = null, timeMax = null, maxResults = 50) => {
  const params = new URLSearchParams();
  if (timeMin) params.append('timeMin', timeMin);
  if (timeMax) params.append('timeMax', timeMax);
  params.append('maxResults', maxResults.toString());
  return fetchAPI(`/calendar/events?${params.toString()}`);
};

/**
 * Obtiene un evento específico por ID
 */
export const getCalendarEvent = async (eventId) => {
  return fetchAPI(`/calendar/events/${eventId}`);
};

/**
 * Crea un nuevo evento en el calendario
 */
export const createCalendarEvent = async (eventData) => {
  return fetchAPI('/calendar/events', {
    method: 'POST',
    body: JSON.stringify(eventData),
  });
};

/**
 * Actualiza un evento existente
 */
export const updateCalendarEvent = async (eventId, eventData) => {
  return fetchAPI(`/calendar/events/${eventId}`, {
    method: 'PUT',
    body: JSON.stringify(eventData),
  });
};

/**
 * Elimina un evento
 */
export const deleteCalendarEvent = async (eventId) => {
  return fetchAPI(`/calendar/events/${eventId}`, {
    method: 'DELETE',
  });
};

// ============================================================================
// INTEGRATIONS CONFIG API
// ============================================================================

/**
 * Obtiene la configuración de integraciones
 */
export const getIntegrationsConfig = async () => {
  return fetchAPI('/integrations/config');
};

/**
 * Guarda la configuración de integraciones
 */
export const saveIntegrationsConfig = async (config) => {
  return fetchAPI('/integrations/config', {
    method: 'POST',
    body: JSON.stringify(config),
  });
};

export default apiService;

